import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Users, FolderKanban,
  X, User, LogOut, ChevronRight, ChevronDown, ChevronUp, KeyRound, LayoutDashboard, Trophy
} from 'lucide-react';
import BASE_URL from '../services/api';

// Section list — mirrors <Section num=...> tags in IntegratedDashboard.jsx
const DASHBOARD_SECTIONS = [
  { num: '1',    title: 'Population' },
  { num: '2',    title: 'Literacy rate' },
  { num: '3',    title: 'Drop-out ratio' },
  { num: '4',    title: 'School Admission (ADW)' },
  { num: '4(a)', title: 'School Admission (TW)' },
  { num: '5',    title: 'Hostel — ADW' },
  { num: '5(a)', title: 'Hostel — TW' },
  { num: '6',    title: 'Free House Site Pattas' },
  { num: '7',    title: 'Court Cases' },
  { num: '8',    title: 'CM Cell Petitions' },
  { num: '9',    title: 'RTI' },
  { num: '10',   title: 'Pending files at Govt' },
  { num: '11.1', title: 'Burial grounds — SC' },
  { num: '11.2', title: 'Burial grounds — ST' },
  { num: '12',   title: 'Transport / Water' },
  { num: '14',   title: 'Buildings' },
  { num: '17',   title: 'DAPSC/ST — DMC' },
  { num: '18',   title: 'Vigilance Committees' },
  { num: '18a',  title: 'Atrocity / Awareness' },
  { num: '19',   title: 'DW Committee' },
  { num: '20',   title: 'Cleanliness Committee' },
  { num: '21',   title: 'Community Certificate' },
  { num: '22',   title: 'POA Claims' },
  { num: '23',   title: 'Loan Applications' },
  { num: '24',   title: 'Construction Status' },
  { num: '25',   title: 'FRA' },
  { num: '26',   title: 'Other Issues' },
];

const Sidebar = ({ isSidebarOpen, setIsSidebarOpen, isCollapsed = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [userName, setUserName] = useState('');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [hasPortals, setHasPortals] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  // Section-jump submenu (only relevant on the Integrated Dashboard route)
  const onDashboard = location.pathname === '/integrated-dashboard';
  const [showSections, setShowSections] = useState(false);
  const [activeSection, setActiveSection] = useState('1');

  // Auto-open sections list when landing on dashboard, close when leaving
  useEffect(() => {
    setShowSections(onDashboard);
  }, [onDashboard]);

  // Highlight the section closest to the top of the viewport
  useEffect(() => {
    if (!onDashboard) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => Math.abs(a.boundingClientRect.top) - Math.abs(b.boundingClientRect.top));
        if (visible[0]) setActiveSection(visible[0].target.id.replace('section-', ''));
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0 }
    );
    // Delay so the dashboard has mounted its sections
    const timer = setTimeout(() => {
      DASHBOARD_SECTIONS.forEach(s => {
        const el = document.getElementById(`section-${s.num}`);
        if (el) observer.observe(el);
      });
    }, 300);
    return () => { clearTimeout(timer); observer.disconnect(); };
  }, [onDashboard]);

  const jumpToSection = (num) => {
    const el = document.getElementById(`section-${num}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setIsSidebarOpen(false);
  };

  useEffect(() => {
    setUserName(localStorage.getItem('userName') || 'User');
    const stored = localStorage.getItem('portals');
    const portalList = stored ? JSON.parse(stored) : [];
    setHasPortals(portalList.length > 0);
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setIsSuperAdmin(user.is_superadmin === true);
  }, []);

  const handleLogout = async () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    try {
      await fetch(`${BASE_URL}/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ user_id: user.id }),
      });
    } catch (err) {
      // still logout even if API fails
    } finally {
      localStorage.removeItem('user');
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('token');
      localStorage.removeItem('userName');
      localStorage.removeItem('username');
      localStorage.removeItem('userRole');
      localStorage.removeItem('portals');
      navigate('/');
    }
  };

  const getMenuItems = () => {
    const items = [
      { name: 'Integrated Dashboard', path: '/integrated-dashboard', icon: LayoutDashboard },
      { name: 'District Ranking',     path: '/district-ranking',     icon: Trophy },
      { name: 'My Apps',              path: '/my-apps',              icon: FolderKanban },
    ];
    if (isSuperAdmin) {
      items.push({ name: 'User Management', path: '/user-management', icon: Users });
    }
    return items;
  };

  const menuItems = getMenuItems();
  const isActive = (path) => location.pathname === path;
  const isSubmenuActive = (submenu) => submenu?.some(item => location.pathname === item.path);
  const toggleSubmenu = (itemName) => setOpenSubmenu(openSubmenu === itemName ? null : itemName);

  return (
    <>
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50 ${isCollapsed ? 'w-20' : 'w-72'} bg-[#0f2e4d] border-r border-[#0a2340] shadow-lg
        transform transition-all duration-300 ease-in-out
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className={`h-16 flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} px-4 border-b border-white/10`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                <img src="Images/tn-logo.png" alt="TN Logo" className="w-8 h-8 object-contain" />
              </div>
              {!isCollapsed && (
                <div>
                  <h1 className="text-sm font-bold text-white leading-tight">Social Justice</h1>
                  <p className="text-[11px] text-blue-200 leading-tight">Department</p>
                </div>
              )}
            </div>
            {!isCollapsed && (
              <button onClick={() => setIsSidebarOpen(false)} className="lg:hidden text-blue-200 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-6 px-3">
            <div className="space-y-1">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                const hasSubmenu = item.submenu && item.submenu.length > 0;
                const submenuOpen = openSubmenu === item.name;
                const submenuActive = isSubmenuActive(item.submenu);

                return (
                  <div key={item.name}>
                    {hasSubmenu ? (
                      <button
                        onClick={() => toggleSubmenu(item.name)}
                        title={isCollapsed ? item.name : undefined}
                        className={`
                          w-full flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} gap-3 px-3 py-3 rounded-md transition-all duration-200
                          ${submenuActive || submenuOpen ? 'bg-gradient-to-r from-[#3d82c4] to-[#2d6fad] text-white shadow-md' : 'text-blue-50 hover:bg-white/10 hover:text-white'}
                        `}
                      >
                        <div className="flex items-center gap-3">
                          <Icon className="w-5 h-5 flex-shrink-0" />
                          {!isCollapsed && <span className="font-medium text-sm">{item.name}</span>}
                        </div>
                        {!isCollapsed && (
                          <ChevronRight className={`w-4 h-4 transition-transform duration-200 ${submenuOpen ? 'rotate-90' : ''}`} />
                        )}
                      </button>
                    ) : (
                      <Link
                        to={item.path}
                        onClick={() => setIsSidebarOpen(false)}
                        title={isCollapsed ? item.name : undefined}
                        className={`
                          flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} gap-3 px-3 py-3 rounded-md transition-all duration-200
                          ${active ? 'bg-gradient-to-r from-[#3d82c4] to-[#2d6fad] text-white shadow-md' : 'text-blue-50 hover:bg-white/10 hover:text-white'}
                        `}
                      >
                        <div className="flex items-center gap-3">
                          <Icon className="w-5 h-5 flex-shrink-0" />
                          {!isCollapsed && <span className="font-medium text-sm">{item.name}</span>}
                        </div>
                        {!isCollapsed && active && <ChevronRight className="w-4 h-4" />}
                      </Link>
                    )}

                    {hasSubmenu && submenuOpen && !isCollapsed && (
                      <div className="ml-4 mt-1 space-y-1">
                        {item.submenu.map((subItem) => {
                          const SubIcon = subItem.icon;
                          const subActive = isActive(subItem.path);
                          return (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              onClick={() => setIsSidebarOpen(false)}
                              className={`
                                flex items-center gap-3 px-3 py-2.5 rounded-md transition-all duration-200 text-sm
                                ${subActive ? 'bg-[#2d6fad] text-white' : 'text-blue-200 hover:bg-white/10 hover:text-white'}
                              `}
                            >
                              <SubIcon className="w-4 h-4 flex-shrink-0" />
                              <span className="font-medium">{subItem.name}</span>
                            </Link>
                          );
                        })}
                      </div>
                    )}

                  </div>
                );
              })}
            </div>

            {/* Section jump list — separate group, appears only on the Integrated Dashboard route */}
            {onDashboard && !isCollapsed && (
              <>
                <hr className="my-4 border-white/15" />
                <div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setShowSections(v => !v)}
                      className="flex-1 flex items-center justify-between gap-2 px-3 py-1.5 text-[11px] uppercase tracking-wide text-blue-200 hover:text-white"
                    >
                      <span className="font-semibold">Sections ({DASHBOARD_SECTIONS.length})</span>
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showSections ? 'rotate-180' : ''}`} />
                    </button>
                    <button
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      title="Go to top"
                      className="p-1.5 text-blue-200 hover:text-white hover:bg-white/10 rounded"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                  </div>
                  {showSections && (
                    <div className="mt-1 ml-2 border-l border-white/20 pl-2 space-y-0.5">
                      {DASHBOARD_SECTIONS.map(s => {
                        const isCur = s.num === activeSection;
                        return (
                          <button
                            key={s.num}
                            onClick={() => jumpToSection(s.num)}
                            className={`w-full flex items-center gap-2 px-2 py-1.5 text-xs text-left rounded transition-colors
                              ${isCur ? 'bg-white/15 text-white font-semibold' : 'text-blue-100 hover:bg-white/10 hover:text-white'}`}
                          >
                            <span className={`inline-block w-9 shrink-0 font-mono text-[10px] ${isCur ? 'text-white' : 'text-blue-300'}`}>
                              {s.num}
                            </span>
                            <span className="truncate">{s.title}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}
          </nav>

          {/* User Profile & Logout */}
          <div className="border-t border-white/10 p-3">
            {!isCollapsed && (
              <div className="flex items-center gap-3 px-2 py-2 mb-2">
                <div className="w-9 h-9 bg-gradient-to-br from-[#3d82c4] to-[#2d6fad] flex items-center justify-center rounded-full flex-shrink-0">
                  <User className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{userName}</p>
                  <p className="text-[10px] text-blue-200 truncate font-medium">
                    {isSuperAdmin ? 'Super Admin' : 'Portal User'}
                  </p>
                </div>
              </div>
            )}

            {/* Change Password - only for portal users */}
            {hasPortals && (
              <Link
                to="/change-password"
                onClick={() => setIsSidebarOpen(false)}
                title={isCollapsed ? 'Change Password' : undefined}
                className={`
                  w-full flex items-center ${isCollapsed ? 'justify-center' : 'justify-center'} gap-2 px-3 py-2.5 mb-2 rounded-md
                  transition-colors font-medium text-sm
                  ${isActive('/change-password') ? 'bg-[#3d82c4] text-white' : 'bg-white/10 text-blue-100 hover:bg-white/20 hover:text-white'}
                `}
              >
                <KeyRound className="w-4 h-4 flex-shrink-0" />
                {!isCollapsed && <span>Change Password</span>}
              </Link>
            )}

            <button
              onClick={() => setShowLogoutConfirm(true)}
              title={isCollapsed ? 'Logout' : undefined}
              className={`
                w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-md
                bg-red-500/20 text-red-200 hover:bg-red-500/30 hover:text-white transition-colors font-medium text-sm
              `}
            >
              <LogOut className="w-4 h-4 flex-shrink-0" />
              {!isCollapsed && <span>Logout</span>}
            </button>

            {!isCollapsed && (
              <p className="text-[10px] text-blue-300/60 text-center mt-3">
                v1.0.0 · Jul 2026
              </p>
            )}
          </div>
        </div>
      </aside>

      {isSidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setIsSidebarOpen(false)} />
      )}

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-800">Confirm Logout</h2>
            </div>
            <div className="p-6">
              <div className="bg-red-50 border border-red-200 p-4">
                <p className="text-sm text-red-700">
                  Are you sure you want to logout? You will need to sign in again to access the portal.
                </p>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => { setShowLogoutConfirm(false); handleLogout(); }}
                className="px-4 py-2 bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                Yes, Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;